import { Time } from '@poseidon/components/StrokeIcons';

import type { SequenceStep, StepIssue } from '~/components/Sequence/sequenceSteps';
import { DEFAULT_DEADLINE_DAYS, stepLabel } from '~/components/Sequence/sequenceSteps';
import { DurationDaysInput } from '~/components/Sequence/DurationDaysInput';
import { SequencePillPopover } from '~/components/Sequence/SequencePillPopover';
import { SequenceStepIssueTooltip } from '~/components/Sequence/SequenceStepIssueTooltip';

interface Props {
  /** The `await_connect` step. */
  step: SequenceStep;
  /** Shared chip classes (grab cursor, hover shadow, invalid ring) from the node. */
  chipBase: string;
  /** A validation problem with this step (shown as the pill's tooltip), or null. */
  invalid: StepIssue | null;
  /** Commit a new acceptance deadline (days). Omit (locked view) → the pill is a static label. */
  onChangeDeadlineDays?: (days: number) => void;
}

/**
 * An `await_connect` ("Await accept") step rendered exactly like {@link SequenceWaitNode}: a compact static
 * pill ("Await accept · up to 5 days") that opens its editable deadline (`DurationDaysInput`) in a portaled
 * popover on click — see {@link SequencePillPopover}. Uses the clock-refresh icon (vs the wait's plain
 * clock) to signal it polls for the invite being accepted. Read-only (no `onChangeDeadlineDays`) is just
 * the label.
 */
export function SequenceAwaitNode({ step, chipBase, invalid, onChangeDeadlineDays }: Props) {
  return (
    <SequencePillPopover
      icon={<Time.ClockRefresh className="size-4 shrink-0 text-lynch-500 dark:text-lynch-400" />}
      label={stepLabel(step)}
      chipBase={chipBase}
      tooltip={
        invalid != null ? (
          <SequenceStepIssueTooltip issue={invalid} />
        ) : (
          'Await acceptance — wait up to this many days for the invite to be accepted'
        )
      }
      editAriaLabel="Edit acceptance deadline">
      {onChangeDeadlineDays != null && (
        <DurationDaysInput
          autoFocus
          key={step.deadlineDays ?? DEFAULT_DEADLINE_DAYS}
          days={step.deadlineDays ?? DEFAULT_DEADLINE_DAYS}
          onCommit={onChangeDeadlineDays}
        />
      )}
    </SequencePillPopover>
  );
}
